import { useCallback } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { QuizQuestion } from "@/features/training/components/track-quiz"

import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { TrackQuiz } from "@/features/training/components/track-quiz"
import { useGetTrainingModules } from "@/features/training/hooks/use-get-training-modules"
import { useUpdateModuleStatus } from "@/features/training/hooks/use-update-module-status"
import { fireTrackCompletionConfetti } from "@/features/training/utils/confetti"

const QUIZ_SECTION_ID = "quiz-ai-awareness"

export function AIAwarenessQuizPage() {
  const { t } = useTranslation("", { keyPrefix: "training.quiz.ai_awareness" })
  const navigate = useNavigate()
  const updateModuleStatus = useUpdateModuleStatus()
  const modulesQuery = useGetTrainingModules()

  // Pool of 7 questions — 4 are randomly selected each attempt
  const allQuestions: QuizQuestion[] = [
    {
      id: "q1",
      question: t("q1.question", "What does AI stand for?"),
      options: [
        { id: "a", label: t("q1.a", "Automated Intelligence") },
        { id: "b", label: t("q1.b", "Artificial Intelligence") },
        { id: "c", label: t("q1.c", "Advanced Integration") },
        { id: "d", label: t("q1.d", "Algorithmic Inference") },
      ],
      correctId: "b",
    },
    {
      id: "q2",
      question: t(
        "q2.question",
        "Which of the following is a key risk when using AI in a work context?"
      ),
      options: [
        { id: "a", label: t("q2.a", "AI always gives correct answers") },
        { id: "b", label: t("q2.b", "AI can generate convincing but incorrect information") },
        { id: "c", label: t("q2.c", "AI cannot process text data") },
        { id: "d", label: t("q2.d", "AI requires no oversight") },
      ],
      correctId: "b",
    },
    {
      id: "q3",
      question: t("q3.question", "What is RAG (Retrieval-Augmented Generation)?"),
      options: [
        { id: "a", label: t("q3.a", "A method to train AI models from scratch") },
        {
          id: "b",
          label: t("q3.b", "A way to combine AI with real data sources for more accurate answers"),
        },
        { id: "c", label: t("q3.c", "A programming language for AI") },
        { id: "d", label: t("q3.d", "A security protocol for AI systems") },
      ],
      correctId: "b",
    },
    {
      id: "q5",
      question: t(
        "q5.question",
        "Why is it important to verify AI-generated content before sharing it?"
      ),
      options: [
        { id: "a", label: t("q5.a", "AI content is always wrong") },
        {
          id: "b",
          label: t("q5.b", "AI can hallucinate — producing plausible but factually incorrect text"),
        },
        { id: "c", label: t("q5.c", "It is legally required in all countries") },
        { id: "d", label: t("q5.d", "Verification is only needed for images, not text") },
      ],
      correctId: "b",
    },
    {
      id: "q6",
      question: t("q6.question", "What does 'prompt engineering' mean?"),
      options: [
        { id: "a", label: t("q6.a", "Building hardware for AI systems") },
        {
          id: "b",
          label: t(
            "q6.b",
            "Crafting clear and effective instructions to get better results from AI"
          ),
        },
        { id: "c", label: t("q6.c", "Programming AI models from scratch") },
        { id: "d", label: t("q6.d", "Deleting data from AI training sets") },
      ],
      correctId: "b",
    },
    {
      id: "q7",
      question: t("q7.question", "Which is a common security concern when using AI tools at work?"),
      options: [
        { id: "a", label: t("q7.a", "AI tools use too much electricity") },
        { id: "b", label: t("q7.b", "AI tools are too slow for business use") },
        {
          id: "c",
          label: t("q7.c", "Sensitive data may be exposed if entered into public AI services"),
        },
        { id: "d", label: t("q7.d", "AI tools cannot connect to the internet") },
      ],
      correctId: "c",
    },
    {
      id: "q8",
      question: t("q8.question", "What is a key benefit of using AI in a business context?"),
      options: [
        { id: "a", label: t("q8.a", "AI eliminates the need for human decision-making") },
        {
          id: "b",
          label: t("q8.b", "AI can help automate repetitive tasks and surface insights faster"),
        },
        { id: "c", label: t("q8.c", "AI guarantees 100% accuracy in all tasks") },
        { id: "d", label: t("q8.d", "AI replaces all software development") },
      ],
      correctId: "b",
    },
  ]

  const handleComplete = useCallback(async () => {
    const existing = modulesQuery.data?.modules.find((m) => m.module_id === QUIZ_SECTION_ID)
    if (!existing || existing.status !== "completed") {
      await updateModuleStatus.mutateAsync({
        moduleId: QUIZ_SECTION_ID,
        data: { status: "completed", completed_at: new Date().toISOString() },
      })
    }

    fireTrackCompletionConfetti()

    setTimeout(() => {
      void navigate("/training")
    }, 2500)
  }, [modulesQuery.data, updateModuleStatus, navigate])

  return (
    <CourseLayout>
      <TrackQuiz questions={allQuestions} trackName="AI Awareness" onComplete={handleComplete} />
    </CourseLayout>
  )
}
